import type JSZip from 'jszip';

/**
 * Pré-flight de imagem: decide pelos **magic bytes** se cada figura é aceita,
 * convertível ou impossível — **antes** do primeiro POST.
 *
 * Por que existe: a validação de estrutura (`exam-import.ts`) confere existência,
 * tamanho e teto por questão, e o dry-run do backend não recebe byte nenhum.
 * Então um `fig02.png` que é AVIF por dentro passava as duas passadas e só
 * estourava no meio da fila de upload, com 422 do `QuestionImageResolver` e a
 * prova pela metade. O `accept` do input não protege disso: o arquivo **está**
 * nomeado `.png`.
 *
 * Quando o formato é convertível, o conserto é local: AVIF (e HEIC no Safari)
 * decodificam nativamente, então `createImageBitmap` + canvas + WebP resolvem sem
 * o admin sair da tela e sem ir buscar o arquivo original.
 */

/** Os únicos mimes que uma imagem de questão pode ter. */
export type AcceptedMime = 'image/png' | 'image/jpeg' | 'image/webp';

/** Para mensagem de erro — a lista, sem repetir literal por aí. */
const ACCEPTED_IMAGE_LABEL = 'PNG, JPEG, WebP';

/**
 * Teto por imagem. Espelha `MAX_IMAGE_BYTES` de
 * `content/application/images/question-image-checks.ts` no monolith — o número
 * de lá é a autoridade, este existe para a recusa acontecer **na hora de
 * escolher o arquivo**, e não depois de o upload viajar e voltar 422 no meio da
 * fila, com a prova pela metade.
 */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/** "12,4 MB" — o separador decimal é o de quem lê a mensagem. */
export function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`;
}

/** O teto em texto, sem casa decimal ("15 MB"). */
export const MAX_IMAGE_LABEL = `${MAX_IMAGE_BYTES / (1024 * 1024)} MB`;

/**
 * O que fazer com um arquivo grande demais. Uma frase só, usada nos três
 * lugares que recusam por tamanho (lote do zip, troca de arquivo, imagem
 * escolhida no formulário) — a instrução é o que falta numa mensagem que só
 * informa o número.
 */
const SHRINK_HINT =
  'Reduza a imagem (exporte em qualidade menor, ou redimensione para a largura que ela realmente precisa ter) e envie o arquivo novo.';

/** Qualidade do WebP gerado na conversão — figura de prova, não fotografia. */
const WEBP_QUALITY = 0.92;

/** Bytes de cabeçalho suficientes para toda assinatura conhecida aqui. */
const HEADER_BYTES = 16;

export type SniffedImage =
  /** Pode subir como está. */
  | { kind: 'accepted'; mime: AcceptedMime; label: string }
  /** O browser decodifica: dá para converter para WebP sem sair da tela. */
  | { kind: 'convertible'; label: 'AVIF' | 'HEIC' | 'GIF' | 'BMP' | 'TIFF' }
  /** Não vira imagem de questão de jeito nenhum. */
  | { kind: 'rejected'; label: string };

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];

/**
 * AVIF e HEIC são o MESMO container (ISO-BMFF, `ftyp` nos bytes 4-7) e se
 * distinguem só pelo **brand** nos bytes 8-11 — tratar os dois como "ftyp" daria
 * a mensagem errada, e são casos diferentes: AVIF converte em qualquer browser
 * moderno, HEIC só no Safari.
 */
const AVIF_BRANDS = new Set(['avif', 'avis']);
const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'mif1',
  'msf1',
  'heim',
  'heis',
]);

/**
 * Espelha `sniffImageMime` do monolith
 * (`src/content/application/images/image-mime.ts`) na parte dos formatos
 * aceitos — mudar um exige mudar o outro. A diferença é de propósito: o back só
 * precisa de "aceito ou não"; aqui a recusa é dividida em "dá para consertar" e
 * "não dá", porque é essa distinção que decide o botão que a UI oferece.
 *
 * SVG é recusado pelo mesmo motivo do back: é XML executável (`<script>` dentro
 * de SVG servido inline é XSS).
 */
export function sniffImageBytes(bytes: Uint8Array): SniffedImage {
  if (startsWith(bytes, PNG_SIGNATURE)) {
    return { kind: 'accepted', mime: 'image/png', label: 'PNG' };
  }
  if (startsWith(bytes, JPEG_SIGNATURE)) {
    return { kind: 'accepted', mime: 'image/jpeg', label: 'JPEG' };
  }
  // WebP é um container RIFF: 'RIFF' nos bytes 0-3 e 'WEBP' nos 8-11 (4-7 são o
  // tamanho do chunk, que varia).
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    return { kind: 'accepted', mime: 'image/webp', label: 'WebP' };
  }

  if (ascii(bytes, 4, 8) === 'ftyp') {
    const brand = ascii(bytes, 8, 12).toLowerCase();
    if (AVIF_BRANDS.has(brand)) return { kind: 'convertible', label: 'AVIF' };
    if (HEIC_BRANDS.has(brand)) return { kind: 'convertible', label: 'HEIC' };
  }
  if (ascii(bytes, 0, 4) === 'GIF8')
    return { kind: 'convertible', label: 'GIF' };
  if (ascii(bytes, 0, 2) === 'BM') return { kind: 'convertible', label: 'BMP' };
  // TIFF: 'II' + 0x2A00 (little endian) ou 'MM' + 0x002A (big endian).
  if (
    startsWith(bytes, [0x49, 0x49, 0x2a, 0x00]) ||
    startsWith(bytes, [0x4d, 0x4d, 0x00, 0x2a])
  ) {
    return { kind: 'convertible', label: 'TIFF' };
  }

  if (ascii(bytes, 0, 5) === '%PDF-') return { kind: 'rejected', label: 'PDF' };
  if (looksLikeSvg(bytes)) return { kind: 'rejected', label: 'SVG' };
  return { kind: 'rejected', label: 'formato desconhecido' };
}

/** Sniff a partir de um `Blob`/`File`, lendo só o cabeçalho. */
async function sniffBlob(blob: Blob): Promise<SniffedImage> {
  const header = await blob.slice(0, HEADER_BYTES).arrayBuffer();
  return sniffImageBytes(new Uint8Array(header));
}

function startsWith(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((byte, index) => bytes[index] === byte);
}

function ascii(bytes: Uint8Array, from: number, to: number): string {
  if (bytes.length < to) return '';
  let out = '';
  for (let i = from; i < to; i += 1) out += String.fromCharCode(bytes[i]!);
  return out;
}

/** `<?xml …><svg>` ou `<svg` direto, tolerando BOM/whitespace na frente. */
function looksLikeSvg(bytes: Uint8Array): boolean {
  const head = ascii(bytes, 0, Math.min(bytes.length, HEADER_BYTES))
    .replace(/^﻿/, '')
    .trimStart()
    .toLowerCase();
  return head.startsWith('<?xml') || head.startsWith('<svg');
}

// ---------------------------------------------------------------------------
// Conversão no browser
// ---------------------------------------------------------------------------

/**
 * Re-encoda para WebP.
 *
 * A falha é **discriminada**, e não um `null`, porque as duas causas pedem
 * coisas diferentes do admin: "este browser não decodifica HEIC" se resolve
 * trocando o arquivo (ou abrindo no Safari); "converteu e ainda passa do teto"
 * se resolve reduzindo a imagem. Com um `null` só, as duas saíam com o texto de
 * decodificação — mandando reduzir quem não tinha problema de tamanho, e vice-versa.
 *
 * `imageOrientation: 'from-image'` não é opcional: sem ele, re-encodar perde a
 * orientação EXIF e a figura sai deitada.
 */
type WebpConversion =
  | { ok: true; blob: Blob; mime: AcceptedMime }
  | { ok: false; why: 'UNDECODABLE' }
  | { ok: false; why: 'STILL_TOO_LARGE'; size: number };

async function convertToWebp(source: Blob): Promise<WebpConversion> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(source, {
      imageOrientation: 'from-image',
    });
  } catch {
    // HEIC fora do Safari cai aqui — o formato é convertível em teoria, não
    // neste browser.
    return { ok: false, why: 'UNDECODABLE' };
  }

  try {
    const encoded = await encodeWebp(bitmap);
    if (!encoded) return { ok: false, why: 'UNDECODABLE' };
    // Não confiar no `type` do blob: browser que não sabe encodar WebP devolve
    // PNG **em silêncio**. O que vale é o que o back vai farejar — os bytes.
    const sniffed = await sniffBlob(encoded);
    if (sniffed.kind !== 'accepted') return { ok: false, why: 'UNDECODABLE' };
    if (encoded.size > MAX_IMAGE_BYTES) {
      return { ok: false, why: 'STILL_TOO_LARGE', size: encoded.size };
    }
    return { ok: true, blob: encoded, mime: sniffed.mime };
  } finally {
    bitmap.close();
  }
}

async function encodeWebp(bitmap: ImageBitmap): Promise<Blob | null> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    return canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
  }
  // Fallback para browser sem OffscreenCanvas — mesmo pipeline, canvas do DOM.
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0);
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), 'image/webp', WEBP_QUALITY),
  );
}

// ---------------------------------------------------------------------------
// Pré-flight do lote
// ---------------------------------------------------------------------------

/**
 * Uma imagem pronta para virar payload. Dois casos porque a memória importa:
 * imagem aceita continua **comprimida no zip** (relida quando a fila chegar
 * naquela questão), e só o que passou por conversão/troca fica em memória.
 */
export interface PreparedInMemory {
  source: 'memory';
  blob: Blob;
  mime: AcceptedMime;
  /** O que aconteceu com ela, para a UI dizer ao admin. */
  note: string;
}

export type PreparedImage =
  | { source: 'zip'; entry: JSZip.JSZipObject; mime: AcceptedMime }
  | PreparedInMemory;

/** O mínimo que o pré-flight precisa saber de uma linha do CSV. */
export interface PreflightRow {
  position: number;
  imageFilenames: string[];
}

export interface PreflightIssue {
  filename: string;
  /** `position`s das questões que citam a imagem — o card diz "Questão 14". */
  positions: number[];
  /**
   * `TOO_LARGE` é separado de `UNSUPPORTED` porque a ação é outra: o formato
   * está certo e o arquivo é o certo — o que se pede é **o mesmo conteúdo
   * menor**, não um arquivo diferente. Colapsar os dois em "trocar" mandava o
   * admin procurar outra figura quando bastava reexportar esta.
   */
  reason: 'CONVERTIBLE' | 'UNSUPPORTED' | 'TOO_LARGE';
  /** O que os bytes dizem que é: 'AVIF', 'SVG', 'formato desconhecido'… */
  detected: string;
  message: string;
  /**
   * Os bytes já lidos, guardados **só** no caso convertível — é o que o botão
   * "Converter para WebP" consome, sem descomprimir o zip de novo. Ausente no
   * caso recusado, que não tem conversão para alimentar.
   */
  blob?: Blob;
}

export interface PreflightResult {
  prepared: Map<string, PreparedImage>;
  issues: PreflightIssue[];
}

/**
 * Lê e classifica **toda** imagem citada, uma por vez.
 *
 * Sequencial e descartando os bytes das aceitas de propósito: manter 40 × 15 MB
 * em memória para economizar um `inflate` de milissegundos na hora do upload é o
 * trade-off errado. A imagem aceita é descomprimida duas vezes (aqui e no
 * upload) e isso é barato; a convertível fica em memória porque seus bytes novos
 * não existem no zip.
 */
export async function preflightImages(
  rows: readonly PreflightRow[],
  images: ReadonlyMap<string, JSZip.JSZipObject>,
  onProgress?: (done: number, total: number) => void,
): Promise<PreflightResult> {
  const positionsByFilename = new Map<string, number[]>();
  for (const row of rows) {
    for (const filename of row.imageFilenames) {
      const positions = positionsByFilename.get(filename);
      if (positions) positions.push(row.position);
      else positionsByFilename.set(filename, [row.position]);
    }
  }

  const prepared = new Map<string, PreparedImage>();
  const issues: PreflightIssue[] = [];
  const entries = [...images.entries()];

  for (const [index, [filename, entry]] of entries.entries()) {
    const positions = positionsByFilename.get(filename) ?? [];

    let bytes: Uint8Array;
    try {
      bytes = await entry.async('uint8array');
    } catch {
      issues.push({
        filename,
        positions,
        reason: 'UNSUPPORTED',
        detected: 'ilegível',
        message: `não foi possível ler "${filename}" de dentro do zip — o arquivo pode estar corrompido.`,
      });
      onProgress?.(index + 1, entries.length);
      continue;
    }

    const classified = classifyImage(
      filename,
      positions,
      sniffImageBytes(bytes),
      bytes.length,
      // Aceita = fica comprimida no zip; convertível = os bytes que já leu.
      (mime) => ({ source: 'zip', entry, mime }),
      () => new Blob([bytes as BlobPart]),
    );
    if ('prepared' in classified) prepared.set(filename, classified.prepared);
    else issues.push(classified.issue);

    onProgress?.(index + 1, entries.length);
  }

  return { prepared, issues };
}

/**
 * Classifica um `Blob` que o admin acabou de escolher no "Trocar arquivo": aceito
 * vira `PreparedImage` na hora, convertível vira pendência com botão (a decisão
 * de alterar bytes é sempre do admin, aqui como no lote), recusado continua
 * pendência.
 */
export async function preflightChosenFile(
  filename: string,
  positions: number[],
  file: Blob,
): Promise<ClassifiedImage> {
  const sniffed = await sniffBlob(file);
  return classifyImage(
    filename,
    positions,
    sniffed,
    file.size,
    (mime) => ({
      source: 'memory',
      blob: file,
      mime,
      note: `arquivo trocado (${MIME_LABEL[mime]})`,
    }),
    () => file,
  );
}

const MIME_LABEL: Record<AcceptedMime, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WebP',
};

/**
 * A imagem convertida, pronta para o payload — ou o **motivo** de não ter dado,
 * já em texto de gente. A mensagem sai daqui, e não de cada tela, porque os dois
 * botões de "Converter" (o do formulário e o da importação) mostravam o mesmo
 * texto escrito duas vezes, e um deles mentia no caso de tamanho.
 */
export type ConversionOutcome =
  | { prepared: PreparedInMemory }
  | { failure: { reason: 'UNSUPPORTED' | 'TOO_LARGE'; message: string } };

export async function convertIssue(
  issue: PreflightIssue,
): Promise<ConversionOutcome> {
  if (!issue.blob) {
    return {
      failure: {
        reason: 'UNSUPPORTED',
        message: `não há bytes guardados de "${issue.filename}" para converter — troque o arquivo.`,
      },
    };
  }
  const converted = await convertToWebp(issue.blob);
  if (!converted.ok) {
    return converted.why === 'STILL_TOO_LARGE'
      ? {
          failure: {
            reason: 'TOO_LARGE',
            message: `"${issue.filename}" convertida para WebP ainda tem ${formatMegabytes(converted.size)} — o limite é ${MAX_IMAGE_LABEL} por imagem. ${SHRINK_HINT}`,
          },
        }
      : {
          failure: {
            reason: 'UNSUPPORTED',
            message: `este navegador não conseguiu decodificar ${issue.detected} — troque "${issue.filename}" por PNG, JPEG ou WebP.`,
          },
        };
  }
  return {
    prepared: {
      source: 'memory',
      blob: converted.blob,
      mime: converted.mime,
      note: `convertida de ${issue.detected} para WebP`,
    },
  };
}

export type ClassifiedImage =
  | { prepared: PreparedImage }
  | { issue: PreflightIssue };

/**
 * O veredito e o **texto** do veredito, num lugar só — o lote e a troca de
 * arquivo passam os dois por aqui para o admin nunca ler duas versões da mesma
 * recusa. Quem chama diz como montar a imagem pronta (entrada do zip ou blob em
 * memória) e de onde vêm os bytes de uma eventual conversão.
 */
function classifyImage(
  filename: string,
  positions: number[],
  sniffed: SniffedImage,
  sizeBytes: number,
  toPrepared: (mime: AcceptedMime) => PreparedImage,
  convertibleBlob: () => Blob,
): ClassifiedImage {
  if (sniffed.kind === 'accepted') {
    // Backstop do teto: `validateImages` já confere o tamanho pelo campo
    // `_data.uncompressedSize` do JSZip, que é privado e pode sumir sem aviso —
    // e o arquivo trocado à mão nem passa por lá. Aqui o tamanho é o real.
    if (sizeBytes > MAX_IMAGE_BYTES) {
      return {
        issue: {
          filename,
          positions,
          reason: 'TOO_LARGE',
          detected: formatMegabytes(sizeBytes),
          message: `${filename} tem ${formatMegabytes(sizeBytes)} — o limite é ${MAX_IMAGE_LABEL} por imagem. ${SHRINK_HINT}`,
        },
      };
    }
    return { prepared: toPrepared(sniffed.mime) };
  }

  const extension = filename.includes('.')
    ? `.${filename.split('.').pop()!}`
    : 'sem extensão';

  if (sniffed.kind === 'convertible') {
    return {
      issue: {
        filename,
        positions,
        reason: 'CONVERTIBLE',
        detected: sniffed.label,
        message: `${filename} tem extensão ${extension} mas o conteúdo é ${sniffed.label}. Aceitos: ${ACCEPTED_IMAGE_LABEL}.`,
        blob: convertibleBlob(),
      },
    };
  }

  return {
    issue: {
      filename,
      positions,
      reason: 'UNSUPPORTED',
      detected: sniffed.label,
      message: `${filename} não é ${ACCEPTED_IMAGE_LABEL} (detectado: ${sniffed.label}) — troque o arquivo.`,
    },
  };
}

// ---------------------------------------------------------------------------
// Bytes → payload
// ---------------------------------------------------------------------------

/** Base64 que o endpoint de questão aceita (cru do zip ou data URI da memória). */
export function preparedToBase64(prepared: PreparedImage): Promise<string> {
  return prepared.source === 'zip'
    ? prepared.entry.async('base64')
    : readAsDataUrl(prepared.blob);
}

/** Data URI — o back aceita com ou sem o prefixo e desmonta ele mesmo. */
function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('falha ao ler os bytes da imagem'));
    reader.readAsDataURL(blob);
  });
}
